import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, KeyRound, Loader2 } from "lucide-react";

const MIN_JUST = 20;
const SCOPE_DRONE = "DRONE-V1";
const SCOPE_RELEASE = "2026.10.24-agent-rtl-safety-r2";
const STATE = {
  none: { l: "Absente", c: "#737373" },
  active: { l: "Active", c: "#F59E0B" },
  expired: { l: "Expirée", c: "#A3A3A3" },
  consumed: { l: "Consommée", c: "#3B82F6" },
};

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

// Exception R2 : état réel de l'exception (expirée calculée localement pour l'affichage seulement).
export const exceptionState = (pe, now) => {
  const cur = pe?.current;
  if (!cur) return "none";
  if (cur.status === "active" && now >= new Date(cur.expires_at).getTime()) return "expired";
  return STATE[cur.status] ? cur.status : "none";
};

// Valide côté UI seulement si : exception active non expirée, éligible, 13 autres conditions GO.
// Le Backend reste l'autorité finale (re-contrôle à l'envoi puis juste avant DISPATCH).
export const exceptionUsable = (rd, now) => {
  const pe = rd?.privilege_exception;
  if (!pe || exceptionState(pe, now) !== "active" || !pe.eligible) return false;
  const others = (rd.rows || []).filter((r) => r.key !== pe.condition);
  const priv = (rd.rows || []).find((r) => r.key === pe.condition);
  return others.length === 13 && others.every((r) => r.status === "GO") && priv?.status === "NO-GO"
    && JSON.stringify(rd.blocking?.update) === JSON.stringify([pe.condition]);
};

export function OtaPrivilegeException({ rid, readiness, now, onChange }) {
  const [just, setJust] = useState("");
  const [busy, setBusy] = useState(false);
  const inScope = readiness?.drone_id === SCOPE_DRONE && readiness?.target_release === SCOPE_RELEASE;
  const pe = readiness?.privilege_exception
    || (inScope ? { current: null, eligible: false, condition: "privileges_minimal",
      reasons: ["réponse du serveur sans champ privilege_exception (Backend non à jour ?)"] } : null);
  // Toujours visible sur DRONE-V1 / R2, même non éligible (raisons exactes affichées, création bloquée).
  if (!pe || (!inScope && !pe.eligible && !pe.current)) return null;
  const st = exceptionState(pe, now);
  const cur = pe.current;
  const remaining = cur ? new Date(cur.expires_at).getTime() - now : 0;
  const canCreate = pe.eligible && st !== "active" && just.trim().length >= MIN_JUST && !busy;

  const ttlMin = Math.round((pe.ttl_s || 900) / 60);
  const migration = pe.kind === "migration";
  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/ota/drones/${rid}/privilege-exception`, { release: readiness.target_release, justification: just.trim() });
      toast.success(`${migration ? "Autorisation de migration" : "Exception"} créée (${ttlMin} min, usage unique)`);
      setJust("");
      onChange();
    } catch (e) { toast.error(apiErr(e)); onChange(); } finally { setBusy(false); }
  };

  return (
    <div data-testid="ota-exception" className="bg-[#F59E0B]/5 border border-[#F59E0B]/30 rounded-sm p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-wider text-[#F59E0B] flex items-center gap-1.5">
          <ShieldAlert size={12} /> Exception opérateur — {pe.condition} (sudo reste NO-GO)
        </div>
        <span data-testid="ota-exception-state" className="font-mono text-[10px]" style={{ color: STATE[st].c }}>
          ● {STATE[st].l}
          {st === "active" && <span data-testid="ota-exception-countdown" className="ml-2 text-white">{fmt(remaining)}</span>}
        </span>
      </div>
      <div className="text-[10px] text-[#A3A3A3] leading-relaxed">
        Limitée à {readiness.drone_id} / {readiness.target_release}. Usage unique, {ttlMin} minutes
        {migration ? " — migration SUDO.HARDENING.2 : moteur fixe TRUSTED exigé, constats legacy uniquement, définitivement close après usage" : ""}. Les 13 autres conditions
        doivent être GO ; le serveur les recontrôle à l'envoi puis juste avant le dispatch. Sauvegarde, VERIFY_STATE local et
        rollbacks inchangés.
      </div>
      {cur && (
        <div data-testid="ota-exception-detail" className="font-mono text-[9px] text-[#737373]">
          {cur.id} · par {cur.created_by?.email} · {new Date(cur.created_at).toLocaleTimeString()} · « {cur.justification} »
          {cur.job_id ? ` · job ${cur.job_id}` : ""}
        </div>
      )}
      {!pe.eligible && (
        <div data-testid="ota-exception-reasons" className="text-[10px] text-[#EF4444] space-y-0.5">
          <div>Exception non applicable — création désactivée, « Mettre à jour » bloqué :</div>
          {(pe.reasons?.length ? pe.reasons : ["raison non fournie par le serveur"]).map((r) => <div key={r} className="font-mono">• {r}</div>)}
        </div>
      )}
      {pe.eligible && st !== "active" && (
        <div className="space-y-2">
          <textarea data-testid="ota-exception-justification" value={just} onChange={(e) => setJust(e.target.value)} rows={2}
            placeholder={`Justification obligatoire (≥ ${MIN_JUST} caractères)`}
            className="w-full bg-[#0F0F0F] border border-white/15 rounded-sm px-2 py-1.5 text-[11px] text-white focus:outline-none focus:border-[#F59E0B]/60" />
          <div className="flex items-center justify-between">
            <span data-testid="ota-exception-just-count" className="font-mono text-[9px] text-[#737373]">{just.trim().length}/{MIN_JUST}</span>
            <button data-testid="ota-exception-create" disabled={!canCreate} onClick={create}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/50 text-[#F59E0B] rounded-sm px-3 py-1.5 hover:bg-[#F59E0B]/10 disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} Autoriser exceptionnellement l'OTA R2
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
